import { ref } from 'vue';
import { useTimelineState } from '~/composables/useTimelineState';
import { useHttpRequest } from '~/composables/useHttpRequest';
import { useAuthService } from '~/composables/useAuthService';
import { useTimelineDb } from '~/composables/useTimelineDb';
import { endpoints } from '~/network/endpoints.js';

export function useTimeline() {
    const { user } = useAuthService();
    const {
        timelineInitLoading,
        timelineLocalLoading,
        timelineLoading,
        timelineErrors,
        timelineData,
        dismissingLoading
    } = useTimelineState();


    const anywhereLoading = computed(() => {
        return timelineLoading.value || timelineLocalLoading.value || timelineInitLoading.value;
    })

    const _getDb = async () => {
        timelineInitLoading.value = true;
        if (!user.value || !user.value.id) throw new Error('User not authenticated');
        const dbName = user.value.timelineDbName || `timeline_${user.value.id}`;
        //Add on the user NUXT_PUBLIC_API_URL to the dbName
        const config = useRuntimeConfig().public.atomNuxt;
        if (!config || !config.apiBaseUrl) {
            timelineInitLoading.value = false;
            throw new Error('API URL is not configured');
        }
        if (!dbName) {
            timelineInitLoading.value = false;
            throw new Error('Database name is not set');
        }
        //Append the API URL host to the dbName
        const host = new URL(config.apiBaseUrl).host;
        const finalDbName = `${host}_${dbName}`;
        const res =  await useTimelineDb(finalDbName);
        timelineInitLoading.value = false;
        return res;
    };
    const initDatabase = async () => {
        await _getDb(); // just opens the DB
        await loadFromDatabase();
    };

    const _getLastUpdated = async () => {
        const db = await _getDb();
        return await db.getLastUpdated();
    };

    const _updateLocalDatabase = async (data) => {
        const db = await _getDb();
        await db.upsertItems(data);
        await loadFromDatabase(); // refresh timelineData after sync
    };

    const loadFromDatabase = async () => {
        timelineLocalLoading.value = true;
        const db = await _getDb();
        const allItems = await db.getAll();

        const updatedIds = new Set();
        const existingMap = new Map(timelineData.value.map(item => [item.id, item]));

        allItems.forEach(item => {
            if (item.dismissedAt || item.deletedAt) return;

            updatedIds.add(item.id);
            const parsedLayoutData = parseJsonSafe(item.layoutData);

            if (existingMap.has(item.id)) {
                // Update existing item
                Object.assign(existingMap.get(item.id), {
                    ...item,
                    layoutData: parsedLayoutData
                });
            } else {
                // Add new item
                timelineData.value.push({
                    ...item,
                    layoutData: parsedLayoutData
                });
            }
        });

        // Remove items no longer in DB
        timelineData.value = timelineData.value
            .filter(item => updatedIds.has(item.id))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        timelineLocalLoading.value = false;
    };


    const parseJsonSafe = (json) => {
        try {
            return JSON.parse(json);
        } catch (e) {
            console.warn('Failed to parse layoutData:', json);
            return {};
        }
    };

    const dismissItem = async (id) => {
        const { httpRequest } = useHttpRequest();
        dismissingLoading.value[id] = true;
        try {
            await httpRequest(endpoints.timeline.dismiss(id));
            // Update local DB for immediate UI feedback
            const db = await _getDb();
            const tx = db.db.transaction('timeline', 'readwrite');
            const store = tx.objectStore('timeline');
            const item = await store.get(id);
            if (item) {
                item.dismissedAt = new Date().toISOString();
                await store.put(item);
            }
            await tx.done;
            await loadFromDatabase();
        } catch (e) {
            console.error('Dismissal failed:', e);
            throw e;
        } finally {
            dismissingLoading.value[id] = false;
        }
    };

    const clearAll = async () => {
        const db = await _getDb();
        await db.clearAll();
        timelineData.value = [];
    };

    const sync = async () => {
        timelineLoading.value = true;
        const lastUpdated = await _getLastUpdated();
        const { httpRequest } = useHttpRequest();
        try {
            const res = await httpRequest(endpoints.timeline.sync, { lastUpdated });
            if (res) {
                console.log("Sync response:", res);
                await _updateLocalDatabase(res);
            }
        } catch (e) {
            console.error('Timeline sync error:', e);
            timelineErrors.value = e;
        } finally {

            timelineLoading.value = false;
        }
    };

    return {
        sync,
        clearAll,
        initDatabase,
        timelineData,
        timelineLocalLoading,
        timelineLoading,
        timelineErrors,
        dismissItem,
        loadFromDatabase,
        dismissingLoading,
        anywhereLoading
    };
}
